import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  from,
  Observable,
  split,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

export interface ApolloClientOptions {
  getToken: () => Promise<string | null>;
  graphqlUrl: string;
  /** Pre-hydrated cache (e.g. restored from IndexedDB). Falls back to empty InMemoryCache. */
  cache?: InMemoryCache;
  /**
   * DEV-ONLY: uniform per-operation latency in ms. When > 0, every operation is
   * delayed by this amount so loading / folder background-fill / item load-more
   * are observable locally. The app passes this (gated on its Vite env);
   * the package stays framework-agnostic. Omit / 0 to disable.
   */
  devFakeDelayMs?: number;
  /** Optional telemetry sink. Injected by the app; the package stays dependency-free. */
  onTransportError?: (info: { operationName: string; statusCode?: number }) => void;
}

export function createApolloClient(
  getTokenOrOpts: (() => Promise<string | null>) | ApolloClientOptions,
  graphqlUrl?: string,
) {
  // Support both old signature (getToken, url) and new options object
  let opts: ApolloClientOptions;
  if (typeof getTokenOrOpts === 'function') {
    if (!graphqlUrl) {
      throw new Error(
        'createApolloClient: graphqlUrl is required with legacy (getToken, url) signature',
      );
    }
    opts = { getToken: getTokenOrOpts, graphqlUrl };
  } else {
    opts = getTokenOrOpts;
  }

  const httpLink = new HttpLink({
    uri: opts.graphqlUrl,
  });

  // ─── DEV-ONLY: uniform link-layer latency ──────────────────────────────
  // Passthrough unless the app supplied `devFakeDelayMs > 0` (gated on its Vite
  // env). Delete this block + drop `delayLink` from the chain to remove.
  const fakeDelayMs = opts.devFakeDelayMs ?? 0;
  const delayLink = new ApolloLink((operation, forward) => {
    if (fakeDelayMs <= 0) return forward(operation);
    return new Observable((observer) => {
      let innerSub: { unsubscribe: () => void } | undefined;
      const timer = setTimeout(() => {
        innerSub = forward(operation).subscribe({
          next: (value) => observer.next(value),
          error: (err) => observer.error(err),
          complete: () => observer.complete(),
        });
      }, fakeDelayMs);
      return () => {
        clearTimeout(timer);
        innerSub?.unsubscribe();
      };
    });
  });
  // ───────────────────────────────────────────────────────────────────────

  const authLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      // Check if authorization header already provided in context
      // This allows callers to bypass the token fetch (e.g., signup before setAuth)
      const existingHeaders = operation.getContext().headers;
      if (existingHeaders?.authorization) {
        forward(operation).subscribe(observer);
        return;
      }

      opts.getToken().then((token) => {
        operation.setContext({
          headers: {
            authorization: token ? `Bearer ${token}` : '',
          },
        });
        forward(operation).subscribe(observer);
      });
    });
  });

  const errorLink = new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      for (const err of error.errors) {
        // INACTIVE_ACCOUNT is tagged UNAUTHENTICATED by the backend but must NOT force logout —
        // the paywall (InactiveAccountDialog) needs the session alive so the user can pay.
        if (err.message === 'INACTIVE_ACCOUNT') {
          window.dispatchEvent(new CustomEvent('auth:account-inactive'));
          continue;
        }
        if (
          (err.extensions?.code === 'UNAUTHENTICATED' &&
            err.message !== 'INVALID_TEAM' &&
            err.message !== 'NOT_FOUND_USER') ||
          err.message === 'API_TOKEN_EXPIRED' ||
          err.message === 'API_TOKEN_INVALID'
        ) {
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
      }
    } else {
      try {
        opts.onTransportError?.({
          operationName: operation?.operationName ?? 'unknown',
          statusCode: (error as { statusCode?: number })?.statusCode,
        });
      } catch {
        // Telemetry must never break the link chain.
      }
    }
  });

  // Build terminal link: split subscriptions to WS, queries/mutations to HTTP
  // Derive WS URL from HTTP URL: https → wss, http → ws
  const wsUrl = opts.graphqlUrl.replace(/^http/, 'ws');
  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsUrl,
      lazy: true,
      retryAttempts: Infinity,
      shouldRetry: () => true,
      connectionParams: async () => {
        const token = await opts.getToken();
        if (!token) throw new Error('No auth token — retry after Firebase initializes');
        return { authorization: `Bearer ${token}` };
      },
    }),
  );

  const terminalLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink,
  );

  return new ApolloClient({
    link: from([delayLink, errorLink, authLink, terminalLink]),
    cache: opts.cache ?? new InMemoryCache(),
  });
}
